// Compact, glance-friendly aspect-ratio + pixel formatting for image metadata.
//
// Strategy: prefer the literal reduced ratio (via GCD) when it's already
// small. Fall back to the best small-integer approximation when the reduced
// ratio is unwieldy — that lets a 1100×617 image still be tagged as ≈16:9
// instead of an unreadable 1100:617.

const DEFAULT_MAX = 16;

export interface RatioResult {
  a: number;
  b: number;
  approximate: boolean;
}

export interface SimplifyOptions {
  // Both sides of the reduced ratio must be ≤ this for an "exact" result.
  // Above this, fall back to a best small-integer approximation in [1..max]².
  max?: number;
}

function gcd(a: number, b: number): number {
  let x = Math.abs(Math.trunc(a));
  let y = Math.abs(Math.trunc(b));
  while (y) {
    [x, y] = [y, x % y];
  }
  return x || 1;
}

export function simplifyRatio(
  width: number,
  height: number,
  options: SimplifyOptions = {},
): RatioResult | null {
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
  if (width <= 0 || height <= 0) return null;

  const max = options.max ?? DEFAULT_MAX;
  const g = gcd(width, height);
  const a = Math.round(width / g);
  const b = Math.round(height / g);

  if (a <= max && b <= max) {
    return { a, b, approximate: false };
  }

  // Brute force across [1..max]² is 256 candidates by default — trivial, and
  // simpler than threading continued-fraction convergents.
  const target = width / height;
  let bestP = 1;
  let bestQ = 1;
  let bestError = Infinity;
  for (let p = 1; p <= max; p++) {
    for (let q = 1; q <= max; q++) {
      const error = Math.abs(p / q - target);
      if (error < bestError) {
        bestError = error;
        bestP = p;
        bestQ = q;
      }
    }
  }

  return { a: bestP, b: bestQ, approximate: true };
}

export function formatDimensions(width: number, height: number): string {
  return `${width}×${height}`;
}

export function formatRatio(ratio: RatioResult): string {
  const core = `${ratio.a}:${ratio.b}`;
  return ratio.approximate ? `≈${core}` : core;
}

export function formatPixelsAndRatio(
  width: number,
  height: number,
  options?: SimplifyOptions,
): string {
  const ratio = simplifyRatio(width, height, options);
  const dims = formatDimensions(width, height);
  return ratio ? `${dims} (${formatRatio(ratio)})` : dims;
}

// Pull naturalWidth/Height off an <img> in an onLoad handler. Returns null for
// broken images and SVGs without intrinsic dimensions, so callers can safely
// skip rendering when the value isn't meaningful.
export function readImageDimensions(
  img: HTMLImageElement,
): { width: number; height: number } | null {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (w > 0 && h > 0) return { width: w, height: h };
  return null;
}
